import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal } from '../Modal/Modal';
import { Table } from '../Table/Table';
import { Icons } from '../Icons/Icons';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import { ModalLoader } from '../ModalLoader';
import './ReviewsModal.scss';
import { useActiveLeaderboardEvent } from '../../hooks/useActiveLeaderboardEvent';
import { leaderboardEvents, ParticipantReview } from '../../api/leaderboard-events';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import { exportApi } from '../../api/export';

interface ReviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
  participantId?: number;
  onChange?: () => void;
}

export const ReviewsModal: React.FC<ReviewsModalProps> = ({
  isOpen,
  onClose,
  userName,
  participantId,
  onChange,
}) => {
  const [reviews, setReviews] = useState<ParticipantReview[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const { activeEvent } = useActiveLeaderboardEvent();

  const fetchReviews = useCallback(async () => {
    if (!participantId || !activeEvent?.id) return;
    try {
      const list = await leaderboardEvents.getParticipantReviews(activeEvent.id, participantId);
      setReviews(list);
      setHasLoaded(true);
    } catch (e) {
      showAlert('Failed to load reviews', 'error');
      setHasLoaded(true);
    }
  }, [participantId, showAlert, activeEvent?.id]);

  useEffect(() => {
    if (isOpen && participantId) {
      setHasLoaded(false);
      fetchReviews();
    } else if (!isOpen) {
      setHasLoaded(false);
      setReviews([]);
    }
  }, [isOpen, participantId, fetchReviews]);

  const { isDeleteModalOpen, openDeleteModal, closeDeleteModal, handleConfirmDelete } =
    useDeleteConfirmation(async (id) => {
      if (!participantId || !id || !activeEvent?.id) return;
      try {
        showLoader();
        await leaderboardEvents.deleteParticipantReview(activeEvent.id, participantId, id);
        closeDeleteModal();
        await fetchReviews();
        if (onChange) onChange();
        showAlert('Review deleted', 'success');
      } catch (e) {
        showAlert('Failed to delete review', 'error');
      } finally {
        hideLoader();
      }
    });

  const columns = [
    { id: '#', label: '#', width: '5%' },
    { id: 'office', label: 'Office', width: '25%' },
    { id: 'totalReviews', label: 'Total Reviews', width: '10%' },
    { id: 'area', label: 'Area', width: '40%' },
    { id: 'additionDate', label: 'Addition Date', width: '12%' },
    {
      id: 'action',
      label: 'Action',
      width: '8%',
      render: (row: { id: number }) => (
        <div className="action-icons">
          <span className="delete-icon" onClick={() => openDeleteModal(row.id)}>
            {Icons.Delete}
          </span>
        </div>
      ),
    },
  ];

  const data = useMemo(
    () =>
      reviews.map((r, index) => ({
        id: r.id,
        '#': index + 1,
        office: r.officeName ?? '-',
        totalReviews: r.quantityOfReviews ?? 0,
        area: r.districtName ?? '-',
        additionDate: r.addedAt ? new Date(r.addedAt).toLocaleDateString() : '-',
      })),
    [reviews]
  );

  const handleExportReviews = async () => {
    try {
      if (!participantId || !activeEvent?.id) return;

      const result = await exportApi.participantReviews(activeEvent.id, participantId);

      const sanitizedUserName = userName?.replace(/[^a-zA-Z0-9]/g, '_') || 'user';
      const customFilename = result.filename.includes('.')
        ? result.filename.replace(/(\.[^.]+)$/, `_${sanitizedUserName}$1`)
        : `${result.filename}_${sanitizedUserName}.xlsx`;

      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = customFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showAlert('Export completed successfully', 'success');
    } catch (error) {
      showAlert('Failed to export reviews', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="reviews-modal">
        <div className="reviews-modal-header">
          <h2>{userName ? `${userName}'s Reviews` : 'Reviews'}</h2>
          <button className="btn outline export-button" onClick={handleExportReviews}>
            Export
          </button>
        </div>
        {!hasLoaded ? (
          <ModalLoader />
        ) : data.length === 0 ? (
          <div className="no-data-message">No reviews found</div>
        ) : (
          <Table columns={columns} data={data} />
        )}
      </div>
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ConfirmationModal
          onCancel={closeDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Are you sure you want to delete this review?"
          message="This action cannot be undone. Once deleted, these items will be permanently removed."
        />
      </Modal>
    </Modal>
  );
};
